import React, { useEffect, useState } from "react";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";

function BottomTabButtons({
  rows,
  onPageChange,
  title,
  page,
  pageSize = 20,
}: any) {
  const totalNumberOfPages = Math.max(1, Math.ceil((rows || 0) / pageSize));
  const maxVisibleButtons = 4;
  const isControlled = typeof page === "number" && page > 0;
  const [activePage, setActivePage] = useState(isControlled ? page : 1);

  useEffect(() => {
    if (isControlled) {
      setActivePage(page);
    }
  }, [isControlled, page]);

  useEffect(() => {
    if (!isControlled) return;
    if (activePage > totalNumberOfPages) {
      const next = totalNumberOfPages;
      setActivePage(next);
      onPageChange?.(next);
    }
  }, [activePage, isControlled, onPageChange, totalNumberOfPages]);

  const goToPage = (nextPage: number) => {
    if (nextPage < 1 || nextPage > totalNumberOfPages) return;
    if (!isControlled) {
      setActivePage(nextPage);
    }
    onPageChange?.(nextPage);
  };

  const handlePrevPage = () => goToPage(activePage - 1);
  const handleNextPage = () => goToPage(activePage + 1);
  const handlePageClick = (nextPage: number) => goToPage(nextPage);

  const navBtnClass =
    "inline-flex h-7 w-7 items-center justify-center rounded border border-erp-line bg-white text-erp-muted transition-colors duration-150 hover:border-erp-accent/40 hover:bg-erp-surface hover:text-erp-ink disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-erp-line disabled:hover:bg-white disabled:hover:text-erp-muted";

  const pageBtnClass = (isActive: boolean) =>
    [
      "inline-flex h-7 min-w-7 items-center justify-center rounded px-1.5 text-[12px] tabular-nums transition-colors duration-150",
      isActive
        ? "border-0 bg-[linear-gradient(90deg,#0b1c2c,#123048)] font-semibold text-white shadow-[0_2px_6px_rgba(11,28,44,0.16)]"
        : "border border-erp-line bg-white font-medium text-erp-muted hover:border-erp-accent/40 hover:bg-erp-surface hover:text-erp-ink",
    ].join(" ");

  const getPageButtons = () => {
    const buttons: React.ReactNode[] = [];

    let startPage = Math.max(1, activePage - Math.floor(maxVisibleButtons / 2));
    let endPage = Math.min(
      totalNumberOfPages,
      startPage + maxVisibleButtons - 1,
    );

    if (endPage - startPage + 1 < maxVisibleButtons) {
      startPage = Math.max(1, endPage - maxVisibleButtons + 1);
    }

    if (startPage > 1) {
      buttons.push(
        <button
          key={1}
          type="button"
          onClick={() => handlePageClick(1)}
          className={pageBtnClass(activePage === 1)}
        >
          1
        </button>,
      );
      if (startPage > 2) {
        buttons.push(
          <span
            key="ellipsis-start"
            className="inline-flex h-7 min-w-5 items-center justify-center px-0.5 text-[12px] text-erp-muted"
            aria-hidden
          >
            …
          </span>,
        );
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          type="button"
          onClick={() => handlePageClick(i)}
          className={pageBtnClass(activePage === i)}
          aria-current={activePage === i ? "page" : undefined}
        >
          {i}
        </button>,
      );
    }

    if (endPage < totalNumberOfPages) {
      if (endPage < totalNumberOfPages - 1) {
        buttons.push(
          <span
            key="ellipsis-end"
            className="inline-flex h-7 min-w-5 items-center justify-center px-0.5 text-[12px] text-erp-muted"
            aria-hidden
          >
            …
          </span>,
        );
      }
      buttons.push(
        <button
          key={totalNumberOfPages}
          type="button"
          onClick={() => handlePageClick(totalNumberOfPages)}
          className={pageBtnClass(activePage === totalNumberOfPages)}
        >
          {totalNumberOfPages}
        </button>,
      );
    }

    return buttons;
  };

  const from = rows === 0 ? 0 : (activePage - 1) * pageSize + 1;
  const to = Math.min(activePage * pageSize, rows);

  return (
    <div className="flex w-full flex-col gap-2 font-inter antialiased min-[560px]:flex-row min-[560px]:items-center min-[560px]:justify-between">
      <p className="m-0 text-[12px] text-erp-muted">
        <span className="font-semibold tabular-nums text-erp-ink">
          {(rows || 0).toLocaleString()}
        </span>{" "}
        {title ?? "items"}
        {rows > 0 ? (
          <span className="text-erp-muted">
            {" "}
            ·{" "}
            <span className="tabular-nums">
              {from}–{to}
            </span>
          </span>
        ) : null}
      </p>

      {totalNumberOfPages > 1 ? (
        <div className="flex flex-wrap items-center gap-1">
          <button
            type="button"
            onClick={handlePrevPage}
            disabled={activePage <= 1}
            aria-label="Previous page"
            className={navBtnClass}
          >
            <KeyboardArrowLeftIcon sx={{ fontSize: 18 }} />
          </button>

          {getPageButtons()}

          <button
            type="button"
            onClick={handleNextPage}
            disabled={activePage >= totalNumberOfPages}
            aria-label="Next page"
            className={navBtnClass}
          >
            <KeyboardArrowRightIcon sx={{ fontSize: 18 }} />
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default BottomTabButtons;
